import type { Icon } from '@phosphor-icons/react';
import { CircleNotch, Shield, ShieldCheck, ShieldSlash, ShieldWarning } from '@phosphor-icons/react';

/**
 * Visual configuration for a single collision simulation state.
 *
 * Shared source of truth so the project-detail panel and the compact
 * list/table status tag stay visually consistent. `icon` is a Phosphor
 * component reference so each consumer can size/style it as needed
 * (capitalize at the JSX call site, e.g. `const Icon = cfg.icon`).
 */
export interface CollisionStateConfig {
  /** Phosphor icon component for this state (capitalize at the JSX call site). */
  icon: Icon;
  /** Icon text color class (e.g. for the detail panel's larger icon). */
  iconText: string;
  /** Icon circle background class (detail panel). */
  iconBg: string;
  /** Status dot background class (detail panel). */
  dot: string;
  /** Status text color class (detail panel). */
  text: string;
  /** Compact tag background class (list card / table row). */
  tagBg: string;
  /** Compact tag text color class (list card / table row). */
  tagText: string;
}

/**
 * Known collision simulation states mapped to their visual config.
 *
 * Only the three states that carry a verdict spend a colour: green for a clean run, amber for
 * collisions found, red for a failed one. "Running" is not a verdict — it is the absence of one
 * yet — so it stays neutral and lets the spinning icon and the pulsing dot say it is working.
 * That is also what keeps blue off the page: the old blue "running" tint was the last of the
 * bootstrap accent left in the project list.
 */
export const COLLISION_STATE_CONFIG = {
  running: {
    icon: CircleNotch,
    iconText: 'text-gray-500',
    iconBg: 'bg-gray-100',
    dot: 'bg-gray-400 animate-pulse',
    text: 'text-gray-700',
    tagBg: 'bg-gray-100',
    tagText: 'text-gray-700',
  },
  // biome-ignore lint/style/useNamingConvention: keys mirror backend collision_simulation_state values + i18n keys
  complete_ok: {
    icon: ShieldCheck,
    iconText: 'text-emerald-600',
    iconBg: 'bg-emerald-100',
    dot: 'bg-emerald-500',
    text: 'text-emerald-700',
    tagBg: 'bg-emerald-100',
    tagText: 'text-emerald-700',
  },
  // biome-ignore lint/style/useNamingConvention: keys mirror backend collision_simulation_state values + i18n keys
  complete_collisions: {
    icon: ShieldWarning,
    iconText: 'text-amber-600',
    iconBg: 'bg-amber-100',
    dot: 'bg-amber-500',
    text: 'text-amber-700',
    tagBg: 'bg-amber-100',
    tagText: 'text-amber-700',
  },
  error: {
    icon: ShieldSlash,
    iconText: 'text-red-600',
    iconBg: 'bg-red-100',
    dot: 'bg-red-500',
    text: 'text-red-700',
    tagBg: 'bg-red-100',
    tagText: 'text-red-700',
  },
} as const satisfies Record<string, CollisionStateConfig>;

/** Fallback config for unknown / unrecognized states. */
export const COLLISION_FALLBACK_CONFIG: CollisionStateConfig = {
  icon: Shield,
  iconText: 'text-gray-500',
  iconBg: 'bg-gray-100',
  dot: 'bg-gray-400',
  text: 'text-gray-600',
  tagBg: 'bg-gray-100',
  tagText: 'text-gray-600',
};

export type CollisionState = keyof typeof COLLISION_STATE_CONFIG;

/** Returns the visual config for a state, falling back when unknown. */
export const getCollisionStateConfig = (state: string | null | undefined): CollisionStateConfig =>
  state && state in COLLISION_STATE_CONFIG
    ? COLLISION_STATE_CONFIG[state as CollisionState]
    : COLLISION_FALLBACK_CONFIG;

/** Returns true when the state is one of the known collision states. */
export const isKnownCollisionState = (state: string | null | undefined): state is CollisionState =>
  !!state && state in COLLISION_STATE_CONFIG;
